import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9onsbaftmtnfcfh0ajg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Object.fromEntries(
            Array.from(formData.entries()).filter(([, v]) => typeof v === 'string')
          ) as Record<string, string>
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          '提交失败，请稍后重试';
        if (typeof msg === 'string' && msg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setFormError('');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <header className="text-center py-14">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于43423漫画网
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              专注于下拉式漫画免费在线阅读，为漫画爱好者提供流畅的漫画下拉式阅读体验
            </p>
          </header>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <div className="flex flex-col gap-3 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    43423漫画网成立于2024年，是一个专注于<strong>下拉式漫画免费阅读</strong>的在线平台。我们深知传统翻页式漫画阅读的繁琐与不便，因此创新性地推出了<strong>漫画下拉式</strong>上下滑动浏览模式，让读者可以像刷短视频一样流畅地阅读漫画。
                  </p>
                  <p>
                    平台汇集了海量的<strong>下拉式漫画大全</strong>资源，涵盖<strong>韩漫下拉式</strong>、<strong>日漫下拉式</strong>、恋爱、热血、奇幻、搞笑、悬疑、校园、都市等多种类型。无论你喜欢什么风格的<strong>下拉式漫画</strong>，都能在这里找到心仪的作品。
                  </p>
                  <p>
                    我们始终坚持免费原则，所有<strong>漫画下拉式阅读</strong>内容均向用户免费开放。<strong>好看的漫画下拉式在线看</strong>，不需要付费订阅，不需要积分兑换，每一个热爱漫画的人都能畅享阅读乐趣。
                  </p>
                  <p>
                    我们的团队由一群资深的漫画爱好者和技术专家组成，致力于打造国内最好的<strong>下拉式漫画网</strong>。从内容筛选到阅读体验优化，从服务器稳定到更新速度保障，我们在每一个细节上追求极致。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Artistic%20warm%20toned%20abstract%20illustration%20with%20flowing%20lines%20and%20soft%20gradients%20representing%20creativity%20and%20storytelling%2C%20elegant%20minimalist%20design%2C%20amber%20and%20coral%20color%20palette%2C%20dreamy%20atmosphere%2C%20no%20text&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="43423漫画网品牌形象"
                  title="43423漫画网 - 下拉式漫画品牌形象"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              来自真实读者的评价
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300 transition-all duration-200"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <span key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-xs text-primary-500"></i>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t.content}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                如有任何问题或建议，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@43423mh.com</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-customer-service-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">在线客服</h3>
                  <p className="text-xs text-foreground-500">工作日 9:00-18:00</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-question-answer-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">反馈建议</h3>
                  <p className="text-xs text-foreground-500">我们珍惜每一条意见</p>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <h3 className="text-base font-heading font-bold text-foreground-950 mb-5">
                  发送消息
                </h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="flex flex-col gap-4"
                >
                  <input
                    type="text"
                    name="website_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="name" className="text-xs font-medium text-foreground-700">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入姓名"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="email" className="text-xs font-medium text-foreground-700">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入邮箱"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="subject" className="text-xs font-medium text-foreground-700">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="message" className="text-xs font-medium text-foreground-700">
                      消息内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入消息内容，最多500字"
                    ></textarea>
                    <p className="text-[11px] text-foreground-400 text-right">
                      最多500字
                    </p>
                  </div>

                  {formStatus === 'error' && (
                    <p className="text-sm text-accent-600 bg-accent-50 px-4 py-2.5 rounded-lg">
                      {formError}
                    </p>
                  )}
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-700 bg-green-50 px-4 py-2.5 rounded-lg">
                      消息已成功发送！我们会尽快回复您。
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于43423漫画网',
            description: '43423漫画网是一个专注于下拉式漫画免费阅读的在线平台，提供韩漫下拉式、日漫下拉式等多种类型漫画在线阅读。',
            url: 'https://43423mh.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '43423漫画网',
              url: 'https://43423mh.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://43423mh.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          }),
        }}
      />
    </>
  );
}